(ns ui.gantt
  "Gantt of pipeline runs: one compact track per transform, sized for a preview
   pane that shares its height with a graph canvas and a log table. Each row is
   named (`:label`) in a column left of the track.

   Knows nothing about time zones or formatting: the host passes windows and
   `:now` already zoned, and each execution a pre-rendered `:tooltip`."
  (:require
   ["date-fns" :refer [addDays addMinutes addSeconds differenceInCalendarDays
                       differenceInHours differenceInSeconds
                       endOfDay format getHours isAfter isBefore isEqual
                       startOfDay]]
   ["lucide-react" :refer [GripVertical]]
   ["react" :as react]
   [applied-science.js-interop :as jsi]
   [ui.gantt-window :refer [ran-in-window? run-extent schedule-marks tick-step ticks window-hours]]
   [ui.status :as status]
   [ui.status-vocab :refer [normalize-status]]
   [ui.utils :refer [clsx]]))

(def row-height 14)

(def bar-height 6)

;; Reserved above the rows so the schedule chips do not cover the first one.
(def ^:private schedule-lane-height 14)

;; date line 14px + label row 24px + border
(def ^:private header-height 39)

(defn content-height
  "Px the gantt needs to show all `row-count` rows."
  [row-count lane?]
  (+ header-height (if lane? schedule-lane-height 0) (* row-count row-height) 2))

(def ^:private name-column-width 160)

(def ^:private name-tail-length 12)

(defn- middle-truncated
  "`text` truncated in the middle, keeping its end, where similar names differ."
  [text]
  (if (<= (count text) (+ name-tail-length 4))
    [:span {:class "truncate"} text]
    (let [split (- (count text) name-tail-length)]
      [:span {:class "flex min-w-0"}
       [:span {:class "truncate whitespace-pre"} (subs text 0 split)]
       [:span {:class "shrink-0 whitespace-pre"} (subs text split)]])))

(defn- row-names
  "The rows' names, level with their tracks."
  [{:keys [data lane? hovered-master-id anchor-id on-row-hover on-row-click]}]
  [:div {:class "shrink-0 border-r border-kleene-layout-border/50 text-[10px] leading-3.5 select-none"
         :style {:width       name-column-width
                 :padding-top (when lane? schedule-lane-height)}}
   (for [{:keys [id label]} data]
     [:div {:key            id
            :title          label
            :class          (clsx "flex px-2 cursor-pointer transition-colors"
                                  (if (= id anchor-id) "font-medium text-kleene-text-link" "text-kleene-text-secondary")
                                  {"bg-kleene-hover-background text-kleene-text" (= id hovered-master-id)})
            :style          {:height row-height}
            :on-mouse-enter #(when on-row-hover (on-row-hover id))
            :on-mouse-leave #(when on-row-hover (on-row-hover nil))
            :on-click       #(when on-row-click (on-row-click id))}
      [middle-truncated (or label "—")]])])

(defn- tick-x
  "Px from the window's start to `t`."
  [timeline cell-width t]
  (* (differenceInSeconds t (:started-at timeline)) (/ cell-width 3600)))

(defn timeline-grid
  "Gridlines, one per `step-hours`. Stepping with the header's granularity rather
   than always hourly keeps this bounded: a year-wide window is ~8,800 hours, and
   an hourly line for each is a frozen tab, not a slow one."
  [{:keys [timeline cell-width step-hours]}]
  (let [{:keys [started-at ended-at]} timeline
        step (max 1 (or step-hours 1))]
    [:div {:class "absolute flex inset-0"}
     (for [i (range 0 (inc (differenceInHours ended-at started-at)) step)]
       [:div {:key i
              :class "flex-1 h-full"
              :style {:width (* step cell-width)
                      :flex-basis (* step cell-width)}}
        [:div {:class "border-l border-kleene-layout-border/50 h-full w-full"}]])]))

(defn tick-grid
  "Gridlines under the axis labels, `step` seconds apart."
  [{:keys [timeline cell-width step]}]
  [:div {:class "absolute inset-0 pointer-events-none"}
   (for [t (ticks timeline step)]
     [:div {:key   (.getTime t)
            :class "absolute inset-y-0 border-l border-kleene-layout-border/50"
            :style {:left (tick-x timeline cell-width t)}}])])

(defn executions-on-timeline
  "One row's runs, positioned on its track. The whole band is the hover target,
   since that is how a reader asks which transform an unlabelled row is."
  [{:keys [node timeline cell-width now hovered-id hovered? anchor? on-bar-click
           on-bar-hover on-row-hover on-row-click]}]
  (let [{win-start :started-at win-end :ended-at} timeline
        executions (filter #(ran-in-window? % timeline now) (:executions node))]
    [:div {:class          (clsx "relative w-full transition-colors"
                                 {"bg-kleene-hover-background" hovered?})
           :style          {:height row-height}
           :on-mouse-enter #(when on-row-hover (on-row-hover (:id node)))
           :on-mouse-leave #(when on-row-hover (on-row-hover nil))
           :on-click       #(when on-row-click (on-row-click (:id node)))}
     ;; The transform the pane was opened on.
     (when anchor?
       [:div {:class "absolute left-0 inset-y-0 w-0.5 bg-kleene-text-link z-20"}])
     (for [{:keys [started-at finished-at execution-id status scheduled-at tooltip] :as execution} executions
           :when execution-id
           :let [;; Clamp to the window so a run that started before it (or has
                 ;; not finished) still shows the part that is on screen.
                 visible-end   (let [e (or finished-at now)]
                                 (if (isBefore e win-end) e win-end))
                 visible-start (if (isBefore started-at win-start) win-start started-at)
                 start-pos     (* (differenceInSeconds visible-start win-start) (/ cell-width 3600))
                 end-pos       (* (differenceInSeconds visible-end win-start) (/ cell-width 3600))
                 hovered?      (= execution-id hovered-id)
                 bar           (status/status-bar (normalize-status status))]]
       [:div {:key         (str execution-id "-" scheduled-at)
              :id          (str execution-id "-" scheduled-at)
              :role        "button"
              :tab-index   -1
              :title       tooltip
              :aria-label  tooltip
              :class       (clsx "absolute rounded-sm cursor-pointer hover:brightness-110"
                                 (:class bar)
                                 {"ring-2 ring-kleene-text-link z-20" hovered?})
              :style       (merge (:style bar)
                                  {:left   (max 0 start-pos)
                                   :top    (/ (- row-height bar-height) 2)
                                   :height bar-height
                                   ;; Floor of 4px: a two-second job in a 24h window is sub-pixel otherwise.
                                   :width  (max 4 (- end-pos start-pos))})
              :on-click       (fn [e]
                                (.stopPropagation e)
                                (when on-bar-click (on-bar-click execution)))
              :on-mouse-enter #(when on-bar-hover (on-bar-hover execution))
              :on-mouse-leave #(when on-bar-hover (on-bar-hover nil))}])]))

(def hour-cell-class "inline-flex items-baseline *:border-l first:*:border-l-0 border-b border-kleene-layout-border/50 *:border-kleene-layout-border/50 whitespace-nowrap text-ellipsis text-kleene-text-secondary overflow-hidden")

(defn- tick-labels
  "Axis labels on clock marks `step` seconds apart, placed absolutely so a window
   starting off a mark still labels each mark where it is."
  [{:keys [timeline cell-width step]}]
  (let [pattern (if (< step 60) "HH:mm:ss" "HH:mm")]
    [:div {:class "relative h-6 overflow-hidden border-b border-kleene-layout-border/50 text-kleene-text-secondary"}
     (for [t (ticks timeline step)]
       [:div {:key   (.getTime t)
              :class "absolute inset-y-0 border-l border-kleene-layout-border/50 whitespace-nowrap overflow-hidden"
              :style {:left  (tick-x timeline cell-width t)
                      :width (* step (/ cell-width 3600))}}
        [:div {:class "px-2 py-1"}
         (format t pattern)]])]))

(defn timeline-header
  "`step` is the seconds between labels at `:hours` and `:quarters`."
  [{:keys [timeline granularity cell-width step class]}]
  (let [{:keys [started-at ended-at]} timeline
        days-diff (differenceInCalendarDays ended-at started-at)
        hours-diff (differenceInHours ended-at started-at)]
    [:div {:class (clsx "text-xs select-none" class)}
     ;; Drawn for a single day too — the date belongs on the axis. 14px, not a full row.
     [:div {:class "flex flex-nowrap overflow-hidden"}
      (for [i (range (inc days-diff))
            :let [day-start (if (= 0 i) started-at (startOfDay (addDays started-at i)))
                  day-end   (if (= days-diff i) ended-at (endOfDay day-start))
                  width     (* (inc (differenceInSeconds day-end day-start)) (/ cell-width 3600))]]
        [:div {:key i
               :class "inline-flex items-baseline *:border-l first:*:border-l-0 *:border-kleene-layout-border/50 whitespace-nowrap text-ellipsis overflow-hidden"
               :style {:width width
                       :flex-basis width}}
         [:div {:class "px-2 text-[10px] leading-3.5 font-medium"}
          (format day-start "do MMMM yyyy")]])]
     (case granularity
       (:quarters :hours)
       [tick-labels {:timeline timeline :cell-width cell-width :step step}]

       :half-days
       [:div {:class "flex flex-nowrap text-kleene-text-secondary overflow-hidden"}
        (for [i (range (js/Math.ceil (/ (inc hours-diff) 12)))
              :let [distance-to-midday (if (>= (getHours started-at) 12)
                                         (- 24 (getHours started-at))
                                         (- 12 (getHours started-at)))
                    cell-time (cond-> started-at
                                (> i 0) (addMinutes (* 60 (+ distance-to-midday (* (dec i) 12)))))
                    width (cond
                            (= i 0) (* distance-to-midday cell-width)
                            (= i (js/Math.ceil (/ hours-diff 12))) (* cell-width (differenceInHours (addSeconds ended-at 1) cell-time))
                            :else (* cell-width 12))]]
          [:div {:key i
                 :class hour-cell-class
                 :style {:width width
                         :flex-basis width}}
           [:div {:class "px-2 py-1"}
            (format cell-time "HH:mm")]])]

       nil)]))


(defn schedules-render
  "Fire-times as ticks across the track; clicking one zooms to that run.
   `schedule-statuses` is fire-time epoch ms -> the run's outcome keyword."
  [{:keys [schedule-timestamps schedule-statuses timeline cell-width data now on-schedule-click
           on-tick-hover]}]
  [:div {:class "absolute inset-0 z-10 select-none pointer-events-none"}
   (for [{:keys [timestamp pos label? edge]} (schedule-marks schedule-timestamps timeline cell-width data now)
         :let [outcome (get schedule-statuses (.getTime timestamp) :other)
               {:keys [line chip]} (status/status-tick outcome)]]
     ;; `w-0` so `left` is an exact point: an auto width takes the time chip's, and
     ;; a container-level translate would then move the tick by half a label.
     [:div {:key   (str timestamp)
            :class "absolute w-0 inset-y-0 group pointer-events-auto"
            :style {:left pos}}
      ;; A 9px hit area over a 1px line, which is unclickable on its own.
      [:div {:class    "absolute inset-y-0 left-1/2 -translate-x-1/2 w-2.25 cursor-pointer"
             :title    (str "Run scheduled at " (format timestamp "HH:mm")
                            (when (not= outcome :other) (str " · " (status/status-label outcome)))
                            (when edge " — before this window, still running in it")
                            " — click to zoom to this run")
             :on-click       #(when on-schedule-click (on-schedule-click timestamp))
             :on-mouse-enter #(when on-tick-hover (on-tick-hover timestamp))
             :on-mouse-leave #(when on-tick-hover (on-tick-hover nil))}]
      ;; Heavier: it marks a boundary, not a point inside this window.
      [:div {:class (clsx "absolute inset-y-0 left-1/2 pointer-events-none" line
                          (if edge "w-0.5" "w-px"))}]
      (when label?
        ;; opaque and above every tick line, so none shows through the text
        [:div {:class "absolute top-0 left-1/2 z-10 rounded bg-kleene-background pointer-events-none"
               ;; Nudged inward so it is not clipped by the track.
               :style {:transform (if edge "translateX(0)" "translateX(-50%)")}}
         [:div {:class (clsx "text-[10px] leading-3.5 font-medium rounded px-1 whitespace-nowrap" chip)}
          (if edge
            (str "‹ " (format timestamp "HH:mm"))
            (format timestamp "HH:mm"))]])])])

(defn now-marker
  "\"Now\" line, with the future shaded, so a window ending ahead of now does not
   read as empty time."
  [{:keys [timeline cell-width now]}]
  (let [{:keys [started-at ended-at]} timeline]
    (when (and now started-at ended-at)
      (cond
        (isAfter now ended-at) nil

        (isBefore now started-at)
        [:div {:class "absolute inset-0 z-0 bg-kleene-selected-background/25 pointer-events-none"}]

        :else
        (let [pos (* (differenceInSeconds now started-at) (/ cell-width 3600))]
          [:<>
           [:div {:class "absolute inset-y-0 right-0 z-0 bg-kleene-selected-background/25 pointer-events-none"
                  :style {:left pos}}]
           [:div {:class "absolute inset-y-0 z-20 w-px bg-kleene-text-error/70 pointer-events-none"
                  :style {:left pos}}
            [:div {:class "absolute -top-px -left-1 w-2 h-2 rounded-full bg-kleene-text-error/70"}]]])))))

(defn filter-handle
  "Draggable edge of the selected range. Inside the track, not poking up into the
   sticky header, which would cover the grip."
  [{:keys [position right? set-mode label]}]
  ;; `items-start`: this box has no width of its own, so centring would put the
  ;; line half a grip right of the boundary it marks.
  [:div {:class      "absolute inset-y-0 flex flex-col items-start cursor-col-resize z-20"
         :style      {:left position}
         :role       "slider"
         :tab-index  0
         :aria-label label
         :on-mouse-down #(set-mode (if right? :right-handle :left-handle))}
   [:div {:class (clsx "py-1 bg-kleene-text-secondary shrink-0"
                       {"rounded-r" (not right?)
                        "rounded-l -translate-x-full ml-px" right?})}
    [:> GripVertical {:size 12 :class "text-white"}]]
   [:div {:class "grow w-px bg-kleene-text-secondary"}]])

(defn filter-overlay [{:keys [subtimeline timeline cell-width set-mode]}]
  (when subtimeline
    ;; Seconds, like the bars: `differenceInMinutes` truncates, which is 2-3px adrift
    ;; once an hour is 300px wide.
    (let [left-pos (* (differenceInSeconds (:started-at subtimeline) (:started-at timeline))
                      (/ cell-width 3600))
          right-pos (* (differenceInSeconds (:ended-at subtimeline) (:started-at timeline))
                       (/ cell-width 3600))]
      [:div {:class "absolute inset-0 z-20"}
       [:div {:class "absolute inset-y-0 left-0 bg-gray-600/20 pointer-events-none"
              :style {:width left-pos}}]
       [:div {:class "absolute inset-y-0 right-0 bg-gray-600/20 pointer-events-none"
              :style {:left  right-pos}}]
       [filter-handle {:position left-pos
                       :label    "Start of selected range"
                       :set-mode set-mode}]
       [filter-handle {:position right-pos
                       :right?   true
                       :label    "End of selected range"
                       :set-mode set-mode}]])))

(defn- use-scroll-edges
  "`[[more-above? more-below?] measure]` for the scroller behind `ref`; pass
   `measure` as its `on-scroll`."
  [ref]
  (let [[edges set-edges] (react/useState [false false])
        measure (react/useCallback
                 (fn []
                   (when-let [el (jsi/get ref :current)]
                     (let [top  (jsi/get el :scrollTop)
                           rest (- (jsi/get el :scrollHeight) (jsi/get el :clientHeight) top)
                           next [(pos? top) (> rest 1)]]
                       (set-edges #(if (= % next) % next)))))
                 #js [])]
    (react/useEffect
     (fn []
       (if-let [el (jsi/get ref :current)]
         (let [observer (js/ResizeObserver. measure)]
           (.observe observer el)
           (doseq [child (array-seq (jsi/get el :children))]
             (.observe observer child))
           #(.disconnect observer))
         js/undefined))
     #js [])
    [edges measure]))

(defn gantt' [{:keys [data schedules schedule-statuses timeline mode set-mode on-timeline-change subtimeline
                      on-subtimeline-change on-schedule-click anchor-id now
                      loading? hovered-id hovered-master-id on-bar-click on-bar-hover
                      on-row-click on-row-hover on-zoom on-pan empty-content
                      error-content locked?]}]
  (let [track-ref (react/useRef nil)
        scroller-ref (react/useRef nil)
        [[more-above? more-below?] measure-scroll] (use-scroll-edges scroller-ref)
        resize-observer-ref (react/useRef nil)
        [total-width set-total-width] (react/useState 0)
        [dragging set-dragging] (react/useState false)
        [inner-timeline set-inner-timeline] (react/useState nil) ;; { :started-at :ended-at }
        [inner-subtimeline set-inner-subtimeline] (react/useState nil) ;; { :started-at :ended-at }
        reference-pos (react/useRef nil)
        reference-timeline (react/useRef nil)
        ;; So releasing a pan is not also read as a click on the bar under the cursor.
        moved-ref (react/useRef false)
        panned-ref (react/useRef false)
        [hovered-tick set-hovered-tick] (react/useState nil)

        hours-diff      (window-hours inner-timeline)
        hour-cell-width (/ total-width hours-diff)
        minutes-per-px  (/ 1 (/ hour-cell-width 60))
        granularity     (cond
                          (> 300 hour-cell-width 30) :hours
                          (> hour-cell-width 300) :quarters
                          :else :half-days)
        ;; labels at least 50px apart
        tick-seconds    (if (= granularity :quarters)
                          (tick-step (/ hour-cell-width 3600) 50)
                          3600)
        ;; drag step in minutes: one label's worth
        step            (/ tick-seconds 60)
        brushing?       (#{:left-handle :right-handle} mode)
        armed?          (and (= mode :left-handle) (not subtimeline))
        ;; Both gated on the pointer not having moved: the rows are inside the pan
        ;; surface, so every pan would otherwise end in a click on a row.
        dragged?        #(jsi/get moved-ref :current)
        bar-click       (fn [execution]
                          (when (and on-bar-click (not (dragged?)))
                            (on-bar-click execution)))
        row-click       (fn [id]
                          (when (and on-row-click (not (dragged?)))
                            (on-row-click id)))
        schedule-click  (fn [timestamp]
                          (when (and on-schedule-click (not (dragged?)))
                            (on-schedule-click timestamp)))]

    (react/useEffect
     (fn []
       (if dragging
         (let [f (fn [e]
                   (jsi/assoc! moved-ref :current true)
                   (case mode
                     :left-handle
                     (let [client-x (jsi/get e :clientX)
                           start-pos (-> track-ref .-current .getBoundingClientRect .-left)]
                       (set-inner-subtimeline
                        (fn [{:keys [ended-at]}]
                          (let [new-date (addMinutes (:started-at timeline) (* (- client-x start-pos) minutes-per-px))]
                            (if (isAfter new-date ended-at)
                              {:started-at ended-at :ended-at new-date}
                              {:started-at new-date :ended-at ended-at})))))

                     :right-handle
                     (let [client-x (jsi/get e :clientX)
                           start-pos (-> track-ref .-current .getBoundingClientRect .-left)]
                       (set-inner-subtimeline
                        (fn [{:keys [started-at]}]
                          (let [new-date (addMinutes (:started-at timeline) (* (- client-x start-pos) minutes-per-px))]
                            (if (isBefore new-date started-at)
                              {:started-at new-date :ended-at started-at}
                              {:started-at started-at :ended-at new-date})))))

                     :scroll
                     (let [pos-diff   (- (jsi/get reference-pos :current) (jsi/get e :clientX))
                           ref-window (jsi/get reference-timeline :current)
                           ;; Minutes, not whole hours: at high zoom an hour is most of the pane.
                           minutes    (* step (js/Math.round (/ (* pos-diff minutes-per-px) step)))]
                       (when (and ref-window
                                  (not (isEqual (addMinutes (:started-at ref-window) minutes)
                                                (:started-at timeline))))
                         (jsi/assoc! panned-ref :current true)
                         (set-inner-timeline {:started-at (addMinutes (:started-at ref-window) minutes)
                                              :ended-at   (addMinutes (:ended-at ref-window) minutes)})))))

               reset (fn []
                       (set-dragging false)
                       (set-mode :scroll)
                       (jsi/assoc! reference-pos :current nil)
                       (jsi/assoc! reference-timeline :current nil)
                       ;; Cleared next tick, so the click after this mouseup still sees the move.
                       (js/setTimeout #(jsi/assoc! moved-ref :current false) 0))]
           (.addEventListener js/document.body "mousemove" f)
           (.addEventListener js/document.body "mouseup" reset)
           (fn []
             (.removeEventListener js/document.body "mousemove" f)
             (.removeEventListener js/document.body "mouseup" reset)))
         js/undefined))
     #js [mode dragging timeline set-inner-timeline set-inner-subtimeline])

    ;; Esc leaves the range-select gesture. Captured and marked handled, so listeners
    ;; further down (the graph's unpin) leave it alone.
    (react/useEffect
     (fn []
       (if brushing?
         (let [f (fn [e]
                   (when (= "Escape" (jsi/get e :key))
                     (.preventDefault e)
                     (set-mode :scroll)
                     (set-dragging false)))]
           (.addEventListener js/document "keydown" f true)
           #(.removeEventListener js/document "keydown" f true))
         js/undefined))
     #js [brushing? set-mode])

    ;; Native listener: React's onWheel is passive, so it cannot preventDefault
    ;; the browser's own zoom.
    (react/useEffect
     (fn []
       (let [el (jsi/get track-ref :current)
             f  (fn [e]
                  (when (and on-zoom (or (jsi/get e :ctrlKey) (jsi/get e :metaKey)))
                    (.preventDefault e)
                    (when-not locked?
                      (on-zoom (if (pos? (jsi/get e :deltaY)) :out :in)))))]
         (if el
           (do (.addEventListener el "wheel" f #js {:passive false})
               #(.removeEventListener el "wheel" f))
           ;; React accepts only a function or undefined as a cleanup value.
           js/undefined)))
     #js [on-zoom locked?])

    (react/useLayoutEffect
     (fn []
       (jsi/assoc! resize-observer-ref
                   :current
                   (js/ResizeObserver.
                    (fn [entries]
                      (let [entry (first entries)]
                        (when entry
                          (set-total-width (-> entry .-contentRect .-width)))))))
       (.observe (jsi/get resize-observer-ref :current) (jsi/get track-ref :current))
       #(.unobserve (jsi/get resize-observer-ref :current) (jsi/get track-ref :current)))
     #js [])

    ;; Reports only a window the reader dragged to, never the host's own echoed back.
    (react/useEffect
     (fn []
       (when (and (not dragging) (jsi/get panned-ref :current))
         (jsi/assoc! panned-ref :current false)
         (on-timeline-change inner-timeline))
       js/undefined)
     #js [inner-timeline dragging])

    (react/useEffect
     (fn []
       (when (or (not (isEqual (:started-at inner-timeline) (:started-at timeline)))
                 (not (isEqual (:ended-at inner-timeline) (:ended-at timeline))))
         (set-inner-timeline timeline))
       js/undefined)
     #js [timeline])

    (react/useEffect
     (fn []
       (when (and (not dragging)
                  (or (nil? inner-subtimeline)
                      (some identity (vals inner-subtimeline))))
         (on-subtimeline-change inner-subtimeline))
       js/undefined)
     #js [inner-subtimeline dragging])

    (react/useEffect
     (fn []
       (when (or (not (isEqual (:started-at inner-subtimeline) (:started-at subtimeline)))
                 (not (isEqual (:ended-at inner-subtimeline) (:ended-at subtimeline))))
         (set-inner-subtimeline subtimeline))
       js/undefined)
     #js [subtimeline])

    [:div {:class "relative border border-kleene-layout-border/50 bg-kleene-background flex flex-1 flex-col overflow-hidden h-full"}
     (when loading?
       [:div {:class "absolute inset-x-0 top-0 h-0.5 z-40 bg-kleene-text-link/70 animate-kleene-pulse"
              :role  "progressbar"
              :aria-label "Loading runs"}])

     [:div {:key       "timeline"
            :ref       scroller-ref
            :on-scroll measure-scroll
            :tab-index 0
            :role      "application"
            :aria-label "Run timeline. Arrow keys pan, plus and minus zoom."
            :class     "relative flex flex-col w-full h-full overflow-y-auto overflow-x-hidden outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-kleene-text-link"
            :on-key-down (fn [e]
                           (when-not locked?
                             (case (jsi/get e :key)
                               "ArrowLeft"  (when on-pan (.preventDefault e) (on-pan :back))
                               "ArrowRight" (when on-pan (.preventDefault e) (on-pan :forward))
                               ("+" "=")    (when on-zoom (on-zoom :in))
                               ("-" "_")    (when on-zoom (on-zoom :out))
                               nil)))}

      [:div {:class "sticky top-0 z-30 flex bg-kleene-background"}
       [:div {:class "shrink-0 flex items-end px-2 pb-1 border-r border-b border-kleene-layout-border/50 text-[10px] font-medium text-kleene-text-secondary select-none"
              :style {:width name-column-width}}
        "Transform"]
       [:div {:class "grow min-w-0"}
        [timeline-header {:timeline    inner-timeline
                          :granularity granularity
                          :cell-width  hour-cell-width
                          :step        tick-seconds}]]
       (when more-above?
         [:div {:class "absolute top-full inset-x-0 h-3 pointer-events-none bg-linear-to-b from-black/10 dark:from-black/40 to-transparent"}])]

      ;; `grow shrink-0`, not `min-h-0`: grow to fill the pane when there are few rows,
      ;; shrink-0 so more rows than fit keep their height and the container scrolls
      ;; instead of the track clipping them.
       [:div {:class "flex grow shrink-0"}
       [row-names {:data              data
                   :lane?             (seq schedules)
                   :hovered-master-id hovered-master-id
                   :anchor-id         anchor-id
                   :on-row-hover      on-row-hover
                   :on-row-click      row-click}]
       [:div {:ref   track-ref
              :class (clsx "relative grow overflow-hidden"
                           {"cursor-grab"       (and (= mode :scroll) (not locked?))
                            "cursor-col-resize" brushing?
                            "cursor-grabbing"   (and dragging (= mode :scroll))})
              :on-mouse-down (fn [e]
                               (when-not (and locked? (= mode :scroll))
                                 (set-dragging true))
                               (jsi/assoc! moved-ref :current false)
                               (when (= mode :left-handle)
                                 (let [client-x (jsi/get e :clientX)
                                       start-pos (-> track-ref .-current .getBoundingClientRect .-left)]
                                   (set-inner-subtimeline
                                    (fn [tmln]
                                      (assoc tmln :started-at (addMinutes (:started-at inner-timeline) (* (- client-x start-pos) minutes-per-px)))))
                                   (set-mode :right-handle)))
                               (jsi/assoc! reference-pos :current (.-clientX e))
                               (jsi/assoc! reference-timeline :current timeline))}
        (if (= granularity :half-days)
          [timeline-grid {:timeline   inner-timeline
                          :cell-width hour-cell-width
                          :step-hours 12}]
          [tick-grid {:timeline   inner-timeline
                      :cell-width hour-cell-width
                      :step       tick-seconds}])
        [now-marker {:timeline   inner-timeline
                     :cell-width hour-cell-width
                     :now        now}]
        ;; The hovered tick's run, shaded under the bars from first start to last finish.
        (when-let [{:keys [started-at ended-at]} (some-> hovered-tick (run-extent data now))]
          (let [left (tick-x inner-timeline hour-cell-width started-at)]
            [:div {:class "absolute inset-y-0 z-0 pointer-events-none bg-kleene-text-link/10 border-x border-kleene-text-link/50"
                   :style {:left  left
                           :width (max 2 (- (tick-x inner-timeline hour-cell-width ended-at) left))}}]))
        [filter-overlay {:timeline    inner-timeline
                         :subtimeline inner-subtimeline
                         :cell-width  hour-cell-width
                         :set-mode    set-mode}]
        [:div {:class "relative z-10"
               ;; A lane for the schedule chips, only when there are schedules.
               :style {:padding-top (when (seq schedules) schedule-lane-height)}}
         (for [node data]
           [executions-on-timeline {:key          (:id node)
                                    :node         node
                                    :timeline     inner-timeline
                                    :cell-width   hour-cell-width
                                    :now          now
                                    :hovered-id   hovered-id
                                    :hovered?     (= (:id node) hovered-master-id)
                                    :anchor?      (= (:id node) anchor-id)
                                    :on-bar-click bar-click
                                    :on-bar-hover on-bar-hover
                                    :on-row-hover on-row-hover
                                    :on-row-click row-click}])]

        ;; After the rows: both are z-10, so the later sibling gets the pointer.
        [schedules-render {:timeline            inner-timeline
                           :cell-width          hour-cell-width
                           :schedule-timestamps schedules
                           :schedule-statuses   schedule-statuses
                           ;; For the border markers: is this off-window run visible here?
                           :data                data
                           :now                 now
                           :on-schedule-click   schedule-click
                           :on-tick-hover       set-hovered-tick}]]]

      ;; An error covers the chart whatever is under it — stale bars cannot be told
      ;; apart from current ones. No `empty-content` means the host has not fetched
      ;; yet, which is not the same as empty.
      (when-let [overlay (or error-content
                             (when (and empty-content (empty? data) (not loading?))
                               empty-content))]
        [:div {:class "absolute inset-0 z-30 flex items-center justify-center bg-kleene-background/80"}
         overlay])]

     (when more-below?
       [:div {:class "absolute bottom-0 inset-x-0 h-3 z-30 pointer-events-none bg-linear-to-t from-black/10 dark:from-black/40 to-transparent"}])

     (when armed?
       [:div {:class "absolute bottom-2 left-1/2 -translate-x-1/2 z-40 text-xs rounded-full px-3 py-1 bg-kleene-secondary-01 text-white shadow"}
        "Drag across the timeline to select a range · Esc to cancel"])]))

(defn gantt [props]
  [:f> gantt' props])
